import { useTranslate } from '@refinedev/core';
import { Flex, Form, Select, Tag, Tooltip, theme } from 'antd';
import { DisplayMode, type CollectionOut } from '@/api/generated/schemas';
import {
  DISPLAY_MODES,
  sectionDisplayIssues,
  sectionModeLock,
} from './sectionDisplay';

interface SectionDisplayModeFieldProps {
  /** Form.List row index — the field lives at `[index, 'display_mode']`. */
  index: number;
  /** The row's current mode, watched once by the parent for all rows. */
  mode: DisplayMode | null | undefined;
  collectionId: number;
  /** Undefined while the lookup is in flight, or the collection is gone. */
  collection: CollectionOut | undefined;
  /** The category these sections belong to; absent on create. */
  categoryId?: number;
}

/**
 * How the client presents one section. Locked where the server decides the
 * mode for itself, and flagged where it will quietly degrade the pick — in
 * both cases the stored value is still shown rather than rewritten, because
 * the server, not this form, is the authority.
 */
export const SectionDisplayModeField = ({
  index,
  mode,
  collectionId,
  collection,
  categoryId,
}: SectionDisplayModeFieldProps) => {
  const t = useTranslate();
  const { token } = theme.useToken();

  const lock = sectionModeLock(collectionId, categoryId, collection);
  const issues = lock ? [] : sectionDisplayIssues(mode, collection);

  return (
    <Flex vertical align="flex-end" gap={token.marginXXS}>
      <Tooltip
        title={lock ? t(`user_catalog.common.display_mode.${lock}_hint`) : null}
      >
        {/* The span keeps the tooltip reachable while the select is
            disabled — a disabled control fires no pointer events. */}
        <span>
          <Form.Item name={[index, 'display_mode']} style={{ marginBottom: 0 }}>
            <Select
              size="small"
              disabled={lock != null}
              style={{ width: 148 }}
              options={DISPLAY_MODES.map((value) => ({
                label: t(`user_catalog.common.display_mode.${value}`),
                value,
              }))}
            />
          </Form.Item>
        </span>
      </Tooltip>

      {issues.map((issue) => (
        <Tooltip
          key={issue}
          title={t(`user_catalog.common.display_mode.${issue}_hint`)}
        >
          <Tag
            style={{
              marginInlineEnd: 0,
              background: token.colorBgContainer,
              borderColor: token.colorBorder,
              color: token.colorTextSecondary,
            }}
          >
            <span
              style={{
                color: token.colorWarning,
                marginInlineEnd: token.marginXXS,
                fontSize: token.fontSizeSM - 3,
              }}
            >
              ●
            </span>
            {t(`user_catalog.common.display_mode.${issue}`)}
          </Tag>
        </Tooltip>
      ))}
    </Flex>
  );
};
